/**
 * Visit Notes — prompt and data-source administration.
 *
 * TWO SECTIONS, canonical first:
 *
 *  1. CANONICAL prompts (CanonicalNotePromptSection) — `visit-note.canonical.
 *     <format>[.<standard>]`, the keys care-management-gql resolves on the
 *     DEFAULT path. Nine of them (format x standard). Seeded by sql/0019.
 *  2. LEGACY prompts — the original four cards, `visit-note.<type>`, which serve
 *     callers still passing the deprecated `noteType`. Seeded by sql/0013. Kept:
 *     they still run, and admins may have tuned them.
 *
 * The canonical section is FIRST because those are the prompts in use. The page
 * previously showed only the legacy four, so an admin editing "SOAP" was editing
 * a prompt the default path had stopped resolving — a silent no-op, which is a
 * worse failure than a visible one.
 *
 * Each legacy card also binds the visit_note_settings row for its type. That row
 * is shared: a canonical shape's data-source gate resolves to the same row
 * (`settingsNoteTypeFor`), so the toggles are administered once, on the legacy
 * card, and the canonical cards point at them instead of duplicating them.
 *
 * The backend synthesizes an all-on settings row for any note type with no DB
 * row (a missing row means "everything on"), so this page always renders four
 * cards — an unseeded type is configurable, not blank. It also synthesizes one
 * locally as a belt-and-braces default, so a short server response can never
 * drop a card.
 *
 * GraphQL operations (backend resolvers live in care-management-gql):
 *   Query:    AcVisitNoteSettings    → visitNoteSettings[]
 *   Mutation: AcSetVisitNoteSettings → setVisitNoteSettings (full row upsert)
 *   Query:    AcAiModelAliases       → aiModelAliases
 */
import { useMutation, useQuery } from "@apollo/client";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

import LoadingSpinner from "../../components/LoadingSpinner";
import { AdminWall } from "../components/AdminWall";
import { CanonicalNotePromptSection } from "../components/CanonicalNotePromptSection";
import { VisitNoteTypeCard } from "../components/VisitNoteTypeCard";
import {
  AI_MODEL_ALIASES_QUERY,
  ALL_VISIT_NOTE_TYPES,
  SET_VISIT_NOTE_SETTINGS_MUTATION,
  VISIT_NOTE_SETTINGS_QUERY,
  type AiModelAliasesResult,
  type SetVisitNoteSettingsResult,
  type SetVisitNoteSettingsVars,
  type VisitNoteSettings,
  type VisitNoteSettingsResult,
  type VisitNoteType,
} from "../graphql/adminOps";

/** A missing row means every source is enabled — mirror that client-side. */
function defaultSettings(noteType: VisitNoteType): VisitNoteSettings {
  return {
    noteType,
    isProblemsEnabled: true,
    isMedicationsEnabled: true,
    isAllergiesEnabled: true,
    isVitalsEnabled: true,
    isCareGapsEnabled: true,
    isPriorNotesEnabled: true,
    updatedBy: null,
  };
}

export default function VisitNotesPage() {
  const { data, previousData, loading, error, refetch } =
    useQuery<VisitNoteSettingsResult>(VISIT_NOTE_SETTINGS_QUERY, {
      fetchPolicy: "cache-first",
    });

  const { data: aliasData } = useQuery<AiModelAliasesResult>(
    AI_MODEL_ALIASES_QUERY,
    { fetchPolicy: "cache-first" },
  );

  const [setSettings, { loading: saving }] = useMutation<
    SetVisitNoteSettingsResult,
    SetVisitNoteSettingsVars
  >(SET_VISIT_NOTE_SETTINGS_MUTATION, {
    // Write the persisted row straight back into the list query's cache entry so
    // a toggle sticks without a refetch (the AiSummaryPage idiom, adapted to a
    // list).
    //
    // UPSERT, not map: the server legitimately returns FEWER than four rows (the
    // table starts empty and a missing row means "everything on" — this page
    // synthesizes the card locally via defaultSettings). A plain `map` can only
    // replace a row that is already in the list, so the first save for an
    // unseeded note type was dropped, the card re-rendered from the synthesized
    // default, and the toggle appeared to snap back while the server held the
    // saved value.
    update(cache, { data: mutData }) {
      if (!mutData) return;
      const saved = mutData.setVisitNoteSettings;
      const existing = cache.readQuery<VisitNoteSettingsResult>({
        query: VISIT_NOTE_SETTINGS_QUERY,
      });
      if (!existing) return;
      const rows = existing.visitNoteSettings;
      const present = rows.some((row) => row.noteType === saved.noteType);
      cache.writeQuery<VisitNoteSettingsResult>({
        query: VISIT_NOTE_SETTINGS_QUERY,
        data: {
          visitNoteSettings: present
            ? rows.map((row) =>
                row.noteType === saved.noteType ? saved : row,
              )
            : [...rows, saved],
        },
      });
    },
  });

  const rows = (data ?? previousData)?.visitNoteSettings ?? [];
  const byType = new Map(rows.map((r) => [r.noteType, r]));
  const modelAliases = aliasData?.aiModelAliases ?? [];

  const handleSettingsChange = (next: VisitNoteSettings) => {
    // Build the input FIELD BY FIELD, never by spread.
    //
    // `next` originates from a cached query result, and in production Apollo
    // stamps `__typename` onto every cached object. A spread (or a rest that
    // only strips `updatedBy`) carries that `__typename` into the mutation
    // input, where GraphQL input validation rejects it as an unknown field —
    // every data-source toggle fails, for every note type. Field-by-field also
    // means a field the server later adds to the OUTPUT type cannot silently
    // leak into the INPUT type.
    //
    // The mutation replaces the whole row, so send every flag — never a patch.
    const input = {
      noteType: next.noteType,
      isProblemsEnabled: next.isProblemsEnabled,
      isMedicationsEnabled: next.isMedicationsEnabled,
      isAllergiesEnabled: next.isAllergiesEnabled,
      isVitalsEnabled: next.isVitalsEnabled,
      isCareGapsEnabled: next.isCareGapsEnabled,
      isPriorNotesEnabled: next.isPriorNotesEnabled,
    };
    void setSettings({ variables: { input } }).catch((e) => {
      toast.error(
        e instanceof Error ? e.message : "Failed to update visit-note settings",
      );
    });
  };

  return (
    <div>
      <AdminWall error={error} screenLabel="Visit Notes">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-xl font-bold">Visit Notes</h1>
          <button
            type="button"
            onClick={() => void refetch()}
            className="p-2 rounded-lg border border-border hover:bg-accent transition-colors"
            aria-label="Refresh"
          >
            <RefreshCw className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Tune the prompt, model, and chart inputs behind each auto-generated
          visit note. Prompt edits publish a new immutable version; data-source
          changes persist immediately.
        </p>

        {loading && rows.length === 0 ? (
          <LoadingSpinner fullScreen={false} label="Loading visit notes..." />
        ) : error ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3">
            <p className="text-sm text-destructive font-medium">
              Failed to load visit-note settings
            </p>
            <p className="text-xs text-destructive/80 mt-1">{error.message}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            <CanonicalNotePromptSection modelAliases={modelAliases} />

            <div className="flex flex-col gap-3">
              <div>
                <h2 className="text-sm font-bold m-0">
                  Legacy prompts (deprecated requests)
                </h2>
                <p className="text-xs text-muted-foreground mt-1 mb-0">
                  Kept and still live: these four serve callers that pass the
                  deprecated <code>noteType</code> instead of a format and
                  standard, so a prompt tuned here still does what it always did.
                  The <strong>Data sources</strong> toggles on these cards govern
                  BOTH paths — a canonical prompt looks its gate up in the same
                  row.
                </p>
              </div>
              <div className="flex flex-col gap-6">
                {ALL_VISIT_NOTE_TYPES.map((noteType) => (
                  <VisitNoteTypeCard
                    key={noteType}
                    noteType={noteType}
                    settings={byType.get(noteType) ?? defaultSettings(noteType)}
                    modelAliases={modelAliases}
                    savingSettings={saving}
                    onSettingsChange={handleSettingsChange}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </AdminWall>
    </div>
  );
}
